import React, { useEffect, useState } from 'react';
import { AlertCircle, X } from 'lucide-react';
import { getAuthHeaders } from '../utils/auth';

const TYPE_LABELS = {
  sale: 'Sales',
  enrollment: 'Enrollments',
  dividend: 'Dividend payments',
  commission: 'Commissions',
  user: 'User',
  group: 'Groups',
  product: 'Products / Campaigns',
};

const EditImpactModal = ({
  open,
  impactUrl,
  changes,
  title = 'Update record',
  description = 'These linked records will be updated together so Sales and Investor Enrollment stay in sync.',
  confirmLabel = 'Update linked records',
  onClose,
  onConfirm,
}) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [impact, setImpact] = useState(null);

  useEffect(() => {
    if (!open || !impactUrl) return undefined;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      setImpact(null);
      try {
        const response = await fetch(impactUrl, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(changes || {}),
        });
        const result = await response.json();
        if (!cancelled) {
          if (result.success) setImpact(result.data);
          else setError(result.message || 'Failed to load linked changes');
        }
      } catch (err) {
        if (!cancelled) setError('Unable to connect to server. Make sure the backend is running.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, impactUrl, JSON.stringify(changes || {})]);

  const handleConfirm = async () => {
    setSubmitting(true);
    setError('');
    try {
      await onConfirm?.();
    } catch (err) {
      setError(err?.message || 'Failed to update');
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
  };

  if (!open) return null;

  const grouped = (impact?.changes || []).reduce((acc, item) => {
    if (!acc[item.type]) acc[item.type] = [];
    acc[item.type].push(item);
    return acc;
  }, {});

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
      <div className="neo-modal p-6 w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3 text-primary-600 dark:text-primary-400">
            <AlertCircle size={24} />
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text">{title}</h2>
              {impact?.label && (
                <p className="text-sm text-gray-500 dark:text-dark-muted mt-1">{impact.label}</p>
              )}
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-dark-text">
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="overflow-y-auto flex-1 space-y-4">
          {loading ? (
            <p className="text-sm text-gray-500 dark:text-dark-muted">Checking linked records...</p>
          ) : (
            <>
              <p className="text-sm text-gray-600 dark:text-dark-muted">{description}</p>

              {impact?.changes?.length ? (
                Object.entries(grouped).map(([type, items]) => (
                  <div key={type}>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-dark-text mb-2">
                      {TYPE_LABELS[type] || type} ({items.length})
                    </h3>
                    <div className="space-y-3">
                      {items.map((item) => (
                        <div key={`${item.type}-${item.id}-${item.label}`} className="neo-inset p-3">
                          <p className="text-sm font-medium text-gray-800 dark:text-dark-text">{item.label}</p>
                          {item.fields?.length > 0 && (
                            <ul className="mt-2 space-y-1">
                              {item.fields.map((field) => (
                                <li key={field.field} className="text-sm text-gray-600 dark:text-dark-muted">
                                  <span className="font-medium text-gray-800 dark:text-dark-text">{field.field}:</span>{' '}
                                  {field.fromLabel} → {field.toLabel}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                !loading && !error && (
                  <p className="text-sm text-gray-500 dark:text-dark-muted">No linked field changes were detected.</p>
                )
              )}
            </>
          )}
        </div>

        <div className="flex gap-3 mt-6">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2 neo-btn text-gray-700 dark:text-dark-text">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={loading || submitting || !impact}
            className="flex-1 px-4 py-2 neo-btn-primary disabled:opacity-50"
          >
            {submitting ? 'Updating...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditImpactModal;
